import type { Card } from "@amzn/ads-blink-client";
import type { AsyncStatus } from "@react-hookz/web";
import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";
import { useStore } from "zustand";

import { initialUnifiedFilterState } from "../../../../config/filters/unified";
import { useAiRecommendedInsightsLoader } from "../../../../hooks/aiRecommendedInsights/useAiRecommendedInsightsLoader";
import { useUnifiedContentsLoader } from "../../../../hooks/unifiedContent/useUnifiedContentsLoader";
import {
    FeatureFlags,
    FeatureFlagsStore,
} from "../../../../store/featureFlags";
import { isSuccess } from "../../../../utils/api.utils";
import { AiResultsViewer } from "../aiResults/AiResultsViewer";
import { withInsightsRenderer } from "../withInsightsRenderer";

import { UnifiedContentCard } from "./UnifiedContentCard";

interface UnifiedContentsProps {
    isLoaderInView: boolean;
    setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
}

export const UnifiedContents: React.FunctionComponent<UnifiedContentsProps> = (
    props: UnifiedContentsProps,
) => {
    const { isLoaderInView, setAsyncStatus, setTotalCount } = props;
    const [asyncStatus, lazyLoadedInsights, totalResults] =
        useUnifiedContentsLoader({
            isLoaderInView,
        });

    const isAiSearchEnabled = useStore(
        FeatureFlagsStore,
        (state) => state[FeatureFlags.AI_SEARCH],
    );

    const [aiLoadingState, aiCards, _aiRecommendations] =
        useAiRecommendedInsightsLoader();
    const showAiResults =
        isAiSearchEnabled &&
        (aiLoadingState !== "not-executed" || aiCards.length > 0);

    useEffect(() => {
        setAsyncStatus(asyncStatus);
    }, [asyncStatus]);

    useEffect(() => {
        if (isSuccess(asyncStatus)) {
            setTotalCount(totalResults);
        }
    }, [asyncStatus, totalResults]);

    const getCardForContentV2 = (content: Card, index: number) => {
        const filterConfig =
            initialUnifiedFilterState.filters.UNIFIED_TYPE.find(
                (filter) => filter.id === content.header,
            );
        const tag = filterConfig?.tag?.label;
        return (
            <UnifiedContentCard
                key={index}
                content={content}
                index={index}
                tag={tag}
            />
        );
    };

    return (
        <>
            {showAiResults && <AiResultsViewer />}
            {lazyLoadedInsights.map(getCardForContentV2)}
            {asyncStatus === "error" && <div>Failed to load contents</div>}
        </>
    );
};

export const UnifiedContentsViewer = withInsightsRenderer(UnifiedContents, {
    showSearchSortDropdown: true,
});
